class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"], [data-search-clear-btn]');
    this.predictiveSearchResults = this.querySelector("[data-predictive-search]");
    this.allPredictiveSearchInstances = document.querySelectorAll("predictive-search");
    this.cachedResults = {};
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = "";
    this.debounceTimer = null;

    this.setupEventListeners();
  }

  setupEventListeners() {
    if (!this.input) return;

    // Listen to form submit
    if (this.input.form) {
      this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));
      this.input.form.addEventListener("reset", this.onFormReset.bind(this));
    }

    // Direct input typing listener with debounce
    this.input.addEventListener("input", (event) => {
      this.toggleResetButton();
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.onChange(event);
      }, 200);
    });

    this.input.addEventListener("focus", this.onFocus.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));

    // Handle trending search pills / quick keyword chips
    this.addEventListener("click", (event) => {
      const pill = event.target.closest("[data-search-pill]");
      if (pill) {
        const text = pill.getAttribute("data-search-pill");
        if (text && this.input) {
          this.input.value = text;
          this.input.focus();
          this.toggleResetButton();
          this.onChange();
        }
      }
    });

    // Handle clear button
    const clearBtn = this.querySelector("[data-search-clear-btn]");
    if (clearBtn) {
      clearBtn.addEventListener("click", (event) => {
        event.preventDefault();
        this.input.value = "";
        this.searchTerm = "";
        if (this.abortController) {
          this.abortController.abort();
          this.abortController = new AbortController();
        }
        this.closeResults(true);
        clearBtn.classList.add("hidden");
        const idleContainer = this.querySelector("[data-search-idle]");
        if (idleContainer) idleContainer.style.display = "";
        this.input.focus();
      });
    }

    // Auto-focus input when offcanvas drawer opens
    const offcanvas = this.closest(".offcanvas");
    if (offcanvas) {
      offcanvas.addEventListener("shown.bs.offcanvas", () => {
        if (this.input) {
          setTimeout(() => {
            this.input.focus();
          }, 120);
        }
      });
    }
  }

  toggleResetButton() {
    this.resetButton = this.resetButton || this.querySelector('button[type="reset"], [data-search-clear-btn]');
    if (!this.resetButton) return;
    const isHidden = this.resetButton.classList.contains("hidden");
    if (this.input && this.input.value.length > 0 && isHidden) {
      this.resetButton.classList.remove("hidden");
    } else if (this.input && this.input.value.length === 0 && !isHidden) {
      this.resetButton.classList.add("hidden");
    }
  }

  getQuery() {
    return this.input ? this.input.value.trim() : "";
  }

  onChange() {
    this.toggleResetButton();
    const newSearchTerm = this.getQuery();
    const idleContainer = this.querySelector("[data-search-idle]");

    if (!newSearchTerm.length) {
      if (idleContainer) idleContainer.style.display = "";
      this.close(true);
      return;
    } else {
      if (idleContainer) idleContainer.style.display = "none";
    }

    this.searchTerm = newSearchTerm;
    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length) {
      event.preventDefault();
      return;
    }
    const selectedOption = this.querySelector('[aria-selected="true"] a');
    if (selectedOption) {
      event.preventDefault();
      selectedOption.click();
    }
  }

  onFormReset(event) {
    this.searchTerm = "";
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = new AbortController();
    }
    this.closeResults(true);
    const idleContainer = this.querySelector("[data-search-idle]");
    if (idleContainer) idleContainer.style.display = "";
    const clearBtn = this.querySelector("[data-search-clear-btn]");
    if (clearBtn) clearBtn.classList.add("hidden");
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();
    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute("results") === "true") {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case "ArrowUp":
        this.switchOption("up");
        break;
      case "ArrowDown":
        this.switchOption("down");
        break;
      case "Enter":
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === "ArrowUp" || event.code === "ArrowDown") {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute("open")) return;

    const moveUp = direction === "up";
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allVisibleElements = Array.from(
      this.querySelectorAll("li, button.predictive-search__item, .predictive-product-card, .predictive-search__suggestion-pill")
    ).filter((element) => element.offsetParent !== null);
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i < allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    if (this.statusElement) this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];
    if (activeElement) {
      activeElement.setAttribute("aria-selected", "true");
      if (selectedElement) selectedElement.setAttribute("aria-selected", "false");
      if (activeElement.id) this.input.setAttribute("aria-activedescendant", activeElement.id);
    }
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"], a[aria-selected="true"]');
    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.trim().toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const searchUrl = (window.routes && window.routes.predictive_search_url)
      ? window.routes.predictive_search_url
      : '/search/suggest';

    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();

    fetch(
      `${searchUrl}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`,
      {
        signal: this.abortController.signal,
      }
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Predictive search failed with status ${response.status}`);
        }
        return response.text();
      })
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, "text/html");
        const section = doc.querySelector("#shopify-section-predictive-search") ||
                        doc.querySelector("#predictive-search-results") ||
                        doc.body;
        const resultsMarkup = section ? section.innerHTML : text;

        this.allPredictiveSearchInstances.forEach((instance) => {
          instance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.name === "AbortError" || error?.code === 20) {
          return;
        }
        console.error("Predictive search error:", error);
        this.close();
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText = this.loadingText || this.getAttribute("data-loading-text") || "Loading...";

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", "true");
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      if (this.statusElement) this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    if (!this.predictiveSearchResults) {
      this.predictiveSearchResults = this.querySelector("[data-predictive-search]");
    }
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.innerHTML = resultsMarkup;
      this.predictiveSearchResults.removeAttribute("inert");
      this.predictiveSearchResults.style.display = "block";
    }

    this.setAttribute("results", "true");

    const idleContainer = this.querySelector("[data-search-idle]");
    if (idleContainer) idleContainer.style.display = "none";

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    const countElement = this.querySelector("[data-predictive-search-live-region-count-value]");
    if (countElement) {
      this.setLiveRegionText(countElement.textContent);
    }
  }

  open() {
    this.setAttribute("open", "true");
    if (this.input) this.input.setAttribute("aria-expanded", "true");
    this.isOpen = true;

    const idleContainer = this.querySelector("[data-search-idle]");
    if (idleContainer) idleContainer.style.display = "none";

    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.style.display = "block";
    }
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm && this.input) {
      this.input.value = "";
      this.removeAttribute("results");
    }
    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute("aria-selected", "false");

    if (this.input) {
      this.input.setAttribute("aria-activedescendant", "");
      this.input.setAttribute("aria-expanded", "false");
    }

    this.removeAttribute("loading");
    this.removeAttribute("open");

    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.removeAttribute("style");
      this.predictiveSearchResults.setAttribute("inert", "");
      this.predictiveSearchResults.innerHTML = "";
    }

    if (!this.input || !this.input.value.trim().length) {
      const idleContainer = this.querySelector("[data-search-idle]");
      if (idleContainer) idleContainer.style.display = "";
    }
  }
}

customElements.define("predictive-search", PredictiveSearch);
